// Operations Board — the admin Classes surface.
//
// Replaces the spreadsheet-style table with prioritized sections and rich
// cards that answer, within five seconds:
//   • What needs my attention?
//   • What can I do from here?
//   • What happens next?
//
// This component owns rendering only. Data (rows, registry status,
// assignments, counts) and mutations (setStatus, edit, duplicate) are
// passed in from ClassesPage which remains the single wiring point.

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Archive,
  Calendar,
  Copy,
  Eye,
  Files,
  ImageOff,
  Mail,
  Plus,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { STATUS_META, type VisibilityStatus } from "@/lib/classRegistry";
import {
  adminStatusLabel,
  detectVisibilityConflicts,
  type ClassGateRow,
  type VisibilityConflict,
} from "@/lib/publicVisibility";
import {
  evaluateClassReadiness,
  summarizeReadiness,
  enrollmentMinimumNotice,
  isParked,
  type ReadinessSummary,
} from "@/lib/classReadiness";




export interface BoardRow {
  slug: string;
  title: string;
  category: string | null;
  ages: string | null;
  thumbnail: string | null;
  dbId?: string;
  capacity: number | null;
  /** Standard minimum-enrollment target. Advisory only — never automatic. */
  minEnrollment?: number | null;
  /**
   * Operating model — how the class is meant to work. Free / variable-schedule
   * clubs are intentionally different, not incomplete.
   */
  pricingModel?: "paid" | "free";
  scheduleModel?: "fixed" | "variable";
  managementType?: "silver_sage" | "external_provider";

  source: "database" | "legacy" | "hybrid";
  /** Storage-level gate used to detect admin/public visibility conflicts. */
  gate?: ClassGateRow | null;
  registrationEnabled?: boolean;
  /** class_registry.calendar_visible — public calendar exposure. */
  calendarVisible?: boolean | null;

  /* --- operational fields feeding the ADMIN-ONLY readiness engine --- */
  schedule?: string | null;
  days?: string | null;
  time?: string | null;
  location?: string | null;
  room?: string | null;
  pricePerChild?: number | null;
  hasSession?: boolean;
  welcomePacketComplete?: boolean;
}



export type InstructorState = "assigned" | "missing" | "multiple" | "pending" | "external";

export interface BoardCounts {
  /** Seats physically occupied — includes authorized free / comp seats. */
  enrolled: number;
  /** Seats backed by real tuition — the only count the paid minimum uses. */
  paidEnrolled?: number;
  waitlist: number;
  interest: number;
  revenueCents: number;
  /** Enrollment count restricted to the Financial Reporting window. */
  financeEnrolled?: number;
  /** Revenue restricted to the Financial Reporting window. */
  financeRevenueCents?: number;
  nextSessionLabel: string | null;
  updatedAt: string | null;
  /** Seats from past sessions — preserved history, never operational. */
  historical?: number;
  /** Canonical session the `enrolled` number refers to. */
  sessionLabel?: string | null;
}

interface Props {
  rows: BoardRow[];
  statusOf: (slug: string) => VisibilityStatus;
  instructorStateFor: (row: BoardRow) => InstructorState;
  instructorNameFor: (row: BoardRow) => string | null;
  instructorNoteFor?: (row: BoardRow) => string | null;
  countsFor: (row: BoardRow) => BoardCounts;
  query: string;
  onEdit: (row: BoardRow) => void;
  onDuplicate: (row: BoardRow) => void;
  onSetStatus: (row: BoardRow, status: VisibilityStatus) => void;
  onCreateNew: () => void;
}



type BucketKey =
  | "attention"
  | "proposal"
  | "ready"
  | "missing_instructor"
  | "coming_soon"
  | "healthy"
  | "parked"
  | "archived";



const BUCKET_META: Record<
  BucketKey,
  { icon: string; label: string; blurb: string; collapsedByDefault: boolean }
> = {
  attention: {
    icon: "🔥",
    label: "Needs attention",
    blurb: "Fix these first — parents can see them.",
    collapsedByDefault: false,
  },
  proposal: {
    icon: "💡",
    label: "Proposals",
    blurb: "Approved teaching proposals. Admin only — finish these when you're ready.",
    collapsedByDefault: false,
  },
  ready: {
    icon: "🟢",
    label: "Ready to publish",
    blurb: "Everything checks out. One click and families can register.",
    collapsedByDefault: false,
  },

  missing_instructor: {
    icon: "⚠",
    label: "Missing instructor",
    blurb: "Assign someone before publishing.",
    collapsedByDefault: false,
  },
  coming_soon: {
    icon: "🔵",
    label: "Coming soon",
    blurb: "Collecting interest. Watch for a threshold.",
    collapsedByDefault: false,
  },
  healthy: {
    icon: "🌿",
    label: "Published & healthy",
    blurb: "Running fine. Nothing you need to do.",
    collapsedByDefault: true,
  },
  parked: {
    icon: "📦",
    label: "Hidden / preserved",
    blurb:
      "Intentionally parked. Hidden from parents, not staffed, not sold — kept in the backend for future use.",
    collapsedByDefault: true,
  },
  archived: {
    icon: "⚫",
    label: "Archived",
    blurb: "Kept for history.",
    collapsedByDefault: true,
  },
};

const BUCKET_ORDER: BucketKey[] = [
  "attention",
  "proposal",
  "ready",


  "missing_instructor",
  "coming_soon",
  "healthy",
  "parked",
  "archived",
];


/**
 * ADMIN-ONLY readiness for a board row.
 *
 * Delegates to the shared, status-aware engine in `@/lib/classReadiness` so a
 * Coming Soon class is never scolded for the things it is *supposed* to be
 * missing, while a Published class is. Never rendered publicly.
 */
export function readinessForRow(
  row: BoardRow,
  status: VisibilityStatus,
  instructorState: InstructorState,
  conflicts: VisibilityConflict[] = [],
): ReadinessSummary {
  const items = evaluateClassReadiness({
    status,
    pricingModel: row.pricingModel ?? "paid",
    scheduleModel: row.scheduleModel ?? "fixed",
    externalProvider: row.managementType === "external_provider",

    hasInstructor: instructorState === "assigned" || instructorState === "multiple",
    thumbnail: row.thumbnail,
    schedule: row.schedule,
    days: row.days,
    time: row.time,
    location: row.location,
    room: row.room,
    price: row.pricePerChild,
    capacity: row.capacity,
    category: row.category,
    ages: row.ages,
    hasSession: row.hasSession,
    welcomePacketComplete: row.welcomePacketComplete,
    registrationEnabled: row.registrationEnabled,
    acceptsRegistrations: row.gate?.accepts_registrations ?? null,
    calendarVisible: row.calendarVisible ?? null,
    // An external provider's class is intentionally "listed publicly, Silver
    // Sage checkout off" — that is its operating model, not a conflict.
    gateConflicts: conflicts
      .filter(
        (c) => !(row.managementType === "external_provider" && c.key === "interest_only"),
      )
      .map((c) => ({ key: c.key, detail: c.detail })),

  });

  return summarizeReadiness(status, items);
}

function bucketize(
  rows: BoardRow[],
  statusOf: (slug: string) => VisibilityStatus,
  instructorStateFor: (row: BoardRow) => InstructorState,
): Record<BucketKey, BoardRow[]> {
  const out: Record<BucketKey, BoardRow[]> = {
    attention: [],
    proposal: [],
    ready: [],
    missing_instructor: [],
    coming_soon: [],
    healthy: [],
    parked: [],
    archived: [],
  };
  for (const row of rows) {
    const status = statusOf(row.slug);
    const instructor = instructorStateFor(row);
    const readiness = readinessForRow(row, status, instructor);

    if (status === "archived") {
      out.archived.push(row);
      continue;
    }
    if (status === "proposal") {
      out.proposal.push(row);
      continue;
    }
    // A real defect always wins, regardless of lifecycle stage.
    if (readiness.critical > 0) {
      out.attention.push(row);
      continue;
    }
    // Intentionally parked: hidden and preserved. Consistent = nothing to do.
    if (isParked(status)) {
      out.parked.push(row);
      continue;
    }
    if (status === "instructor_wanted" || (status !== "published" && status !== "coming_soon" && instructor === "missing")) {
      out.missing_instructor.push(row);
      continue;
    }

    if (status === "coming_soon") {
      out.coming_soon.push(row);
      continue;
    }
    if (status === "published" || status === "waitlist") {
      if (readiness.warnings > 0) {
        out.attention.push(row);
      } else {
        out.healthy.push(row);
      }
      continue;
    }
    // draft / hidden
    if (readiness.warnings === 0 && readiness.todos === 0) {
      out.ready.push(row);
    } else {
      out.attention.push(row);
    }
  }
  return out;
}


function filterRows(rows: BoardRow[], q: string): BoardRow[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(
    (r) =>
      r.title.toLowerCase().includes(needle) ||
      (r.category ?? "").toLowerCase().includes(needle),
  );
}

function relativeTime(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatMoney(cents: number): string {
  if (!cents) return "$0";
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function OperationsBoard(props: Props) {
  const {
    rows,
    statusOf,
    instructorStateFor,
    instructorNameFor,
    instructorNoteFor,
    countsFor,
    query,
    onEdit,
    onDuplicate,
    onSetStatus,
    onCreateNew,
  } = props;


  const buckets = useMemo(() => {
    const filtered = filterRows(rows, query);
    return bucketize(filtered, statusOf, instructorStateFor);
  }, [rows, query, statusOf, instructorStateFor]);

  const total = rows.length;
  const anyVisible = BUCKET_ORDER.some((k) => buckets[k].length > 0);

  if (total === 0) {
    return (
      <EmptyPage onCreateNew={onCreateNew} />
    );
  }
  if (!anyVisible) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/50 p-10 text-center">
        <p className="text-sm text-muted-foreground">
          Nothing matches "{query}". Try a different search.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {BUCKET_ORDER.map((key) => {
        const list = buckets[key];
        if (list.length === 0) return null;
        return (
          <BucketSection
            key={key}
            bucket={key}
            rows={list}
            statusOf={statusOf}
            instructorStateFor={instructorStateFor}
            instructorNameFor={instructorNameFor}
            instructorNoteFor={instructorNoteFor}
            countsFor={countsFor}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onSetStatus={onSetStatus}
          />

        );
      })}
    </div>
  );
}


function EmptyPage({ onCreateNew }: { onCreateNew: () => void }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center space-y-3">
      <p className="text-base font-medium">No classes yet.</p>
      <p className="text-sm text-muted-foreground">
        Create your first class and it will show up here, sorted by what needs your attention.
      </p>
      <Button onClick={onCreateNew}>
        <Plus className="mr-2 h-4 w-4" />
        New class
      </Button>
    </div>
  );
}

function BucketSection({
  bucket,
  rows,
  statusOf,
  instructorStateFor,
  instructorNameFor,
  instructorNoteFor,
  countsFor,
  onEdit,
  onDuplicate,
  onSetStatus,
}: {
  bucket: BucketKey;
  rows: BoardRow[];
  statusOf: (slug: string) => VisibilityStatus;
  instructorStateFor: (row: BoardRow) => InstructorState;
  instructorNameFor: (row: BoardRow) => string | null;
  instructorNoteFor?: (row: BoardRow) => string | null;
  countsFor: (row: BoardRow) => BoardCounts;
  onEdit: (row: BoardRow) => void;
  onDuplicate: (row: BoardRow) => void;
  onSetStatus: (row: BoardRow, status: VisibilityStatus) => void;
}) {
  const meta = BUCKET_META[bucket];
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-2">
          <h2 className="text-lg font-heading font-semibold text-foreground">
            <span className="mr-1.5">{meta.icon}</span>
            {meta.label}
          </h2>
          <span className="text-xs text-muted-foreground">{rows.length}</span>
        </div>
        <p className="text-xs text-muted-foreground italic hidden sm:block">{meta.blurb}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((row) => (
          <BoardCard
            key={`${row.source}-${row.slug}`}
            row={row}
            status={statusOf(row.slug)}
            instructorState={instructorStateFor(row)}
            instructorName={instructorNameFor(row)}
            instructorNote={instructorNoteFor?.(row) ?? null}
            counts={countsFor(row)}
            bucket={bucket}
            onEdit={() => onEdit(row)}
            onDuplicate={() => onDuplicate(row)}
            onSetStatus={(s) => onSetStatus(row, s)}
          />
        ))}
      </div>

    </section>
  );
}


function BoardCard({
  row,
  status,
  instructorState,
  instructorName,
  instructorNote,
  counts,
  bucket,
  onEdit,
  onDuplicate,
  onSetStatus,
}: {
  row: BoardRow;
  status: VisibilityStatus;
  instructorState: InstructorState;
  instructorName: string | null;
  instructorNote?: string | null;
  counts: BoardCounts;
  bucket: BucketKey;
  onEdit: () => void;
  onDuplicate: () => void;
  onSetStatus: (s: VisibilityStatus) => void;
}) {


  const navigate = useNavigate();
  const meta = STATUS_META[status];
  const conflicts = useMemo(
    () => detectVisibilityConflicts(status, row.gate ?? null, row.registrationEnabled ?? true),
    [status, row.gate, row.registrationEnabled],
  );

  // ADMIN-ONLY readiness. Never rendered on a public surface.
  const readiness = useMemo(
    () => readinessForRow(row, status, instructorState, conflicts),
    [row, status, instructorState, conflicts],
  );
  // Publishing is an explicit human decision AND requires a vetted instructor:
  // a Coming Soon class must never be one click away from paid registration
  // while nobody is assigned to teach it.
  const instructorReady =
    instructorState === "assigned" ||
    instructorState === "multiple" ||
    instructorState === "external";
  const canPublish =
    readiness.critical === 0 &&
    readiness.warnings === 0 &&
    readiness.setupItems === 0 &&
    instructorReady &&
    status !== "published";

  const isPublished = status === "published" || status === "waitlist";

  // Advisory only: never cancels, refunds, transfers, closes registration,
  // postpones a class, or touches payroll. It exists so the owner decides.
  const minEnrollmentNotice = enrollmentMinimumNotice({
    status,
    pricingModel: row.pricingModel ?? "paid",
    minEnrollment: row.minEnrollment,

    enrolled: counts.enrolled,
    paidEnrolled: counts.paidEnrolled ?? counts.enrolled,
    registrationOpen:
      row.registrationEnabled !== false && row.gate?.accepts_registrations !== false,
  });

  const instructorLabel =
    instructorState === "external"
      ? "Privately managed"
      : instructorState === "missing"
        ? isParked(status)
          ? "No instructor (parked)"
          : "Instructor Needed"

        : instructorState === "pending"
          ? "Awaiting acceptance"
          : instructorState === "multiple"
            ? "Multiple instructors"
            : instructorName ?? "Assigned";

  const openRoster = () =>
    navigate(`/admin/registrations?class=${encodeURIComponent(row.slug)}`);
  const openPreview = () =>
    window.open(`/classes?open=${encodeURIComponent(row.slug)}`, "_blank");
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/classes?open=${encodeURIComponent(row.slug)}`,
      );
    } catch {
      /* no-op */
    }
  };

  return (
    <Card className="overflow-hidden flex flex-col hover:shadow-md transition-shadow">
      {/* Cover */}
      <button
        type="button"
        onClick={onEdit}
        className="relative block h-32 w-full overflow-hidden bg-muted text-left group"
        aria-label={`Edit ${row.title}`}
      >
        {row.thumbnail ? (
          <img
            src={row.thumbnail}
            alt=""
            className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
          />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground gap-1.5">
            <ImageOff className="h-6 w-6" />
            <span className="text-xs">No cover image</span>
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge
            variant="outline"
            className={`${conflicts.length ? "bg-amber-50 text-amber-900 border-amber-300" : meta.badgeClass} shadow-sm`}
            title={conflicts.map((c) => c.detail).join(" ") || meta.description}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${meta.dot}`} />
            {adminStatusLabel(status, conflicts)}
          </Badge>
        </div>

      </button>

      {/* Body */}
      <div className="p-4 pt-3 flex-1 flex flex-col gap-3">
        <div>
          <button
            type="button"
            onClick={onEdit}
            className="text-left w-full font-heading text-base font-semibold text-foreground hover:text-primary transition-colors line-clamp-2"
          >
            {row.title}
          </button>
          <p className="text-xs text-muted-foreground mt-0.5">
            {row.category ?? "Uncategorized"}
            {row.ages ? ` · ${row.ages}` : ""}
          </p>
        </div>

        {conflicts.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900 space-y-0.5">
            {conflicts.map((c) => (
              <p key={c.key}>{c.detail}</p>
            ))}
          </div>
        )}


        {/* Instructor line */}
        <div className="flex items-center gap-1.5 text-xs">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span
            className={
              instructorState === "missing"
                ? "text-amber-700 font-medium"
                : instructorState === "pending"
                  ? "text-purple-700"
                  : "text-foreground"
            }
          >
            {instructorLabel}
          </span>
        </div>
        {instructorNote ? (
          <p className="text-[11px] text-muted-foreground -mt-1 pl-5">{instructorNote}</p>
        ) : null}

        {/* Counts */}
        <div className="grid grid-cols-3 gap-2 text-center bg-muted/40 rounded-md py-2">
          <Metric
            label={counts.sessionLabel ? `Registered · ${counts.sessionLabel}` : "Registered"}
            value={counts.enrolled}
            accent={counts.enrolled > 0}
          />
          <Metric label="Interest" value={counts.interest} accent={counts.interest > 0} />
          <Metric label="Historical" value={counts.historical ?? 0} accent={false} />
        </div>

        {(row.capacity != null || (row.minEnrollment ?? 0) > 0) && (
          <p className="-mt-1 text-[11px] text-muted-foreground">
            {row.capacity != null ? `${counts.enrolled} of ${row.capacity} seats occupied` : null}
            {row.capacity != null && (row.minEnrollment ?? 0) > 0 ? " · " : null}
            {(row.minEnrollment ?? 0) > 0
              ? `${counts.paidEnrolled ?? counts.enrolled} of ${row.minEnrollment} paid minimum`
              : null}
          </p>
        )}

        {minEnrollmentNotice && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900">
            <span className="font-medium">{minEnrollmentNotice}</span>
            <span className="opacity-80">
              {" "}
              · your call — nothing has been cancelled, refunded or closed · admin only
            </span>
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {counts.nextSessionLabel ?? "No upcoming session"}
          </span>
          <button
            type="button"
            onClick={() => navigate("/admin/reports")}
            className="text-primary hover:underline"
          >
            View financial details
          </button>
        </div>





        {/* ADMIN-ONLY readiness panel. Status-aware: a Coming Soon class shows
            quiet to-dos, a Published class shows loud problems. */}
        {readiness.items.length > 0 ? (
          <div
            className={`rounded-md border p-2 text-xs ${
              readiness.tone === "critical"
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : readiness.tone === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-border bg-muted/40 text-muted-foreground"
            }`}
          >
            <div className="font-medium mb-1 inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {readiness.headline}
              <span className="opacity-70">· admin only</span>
            </div>
            <ul className="list-disc pl-4 space-y-0.5">
              {readiness.items.slice(0, 4).map((b) => (
                <li key={b.key}>{b.label}</li>
              ))}
              {readiness.items.length > 4 && (
                <li className="opacity-70">+{readiness.items.length - 4} more</li>
              )}
            </ul>
          </div>
        ) : (
          bucket === "ready" && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
              Ready to publish. Families can register the moment you do.
            </div>
          )
        )}


        {/* Actions — flush to bottom */}
        <div className="mt-auto pt-2 flex flex-wrap gap-1.5">
          <Button size="sm" variant="default" onClick={onEdit} className="h-8">
            Edit
          </Button>
          {canPublish && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onSetStatus("published")}
              className="h-8"
            >
              <Eye className="h-3.5 w-3.5 mr-1" />
              Publish
            </Button>
          )}
          {isPublished && (
            <Button size="sm" variant="outline" onClick={openRoster} className="h-8">
              View roster
            </Button>
          )}
          {isPublished && counts.enrolled > 0 && (
            <Button size="sm" variant="outline" onClick={openRoster} className="h-8">
              <Mail className="h-3.5 w-3.5 mr-1" />
              Email families
            </Button>
          )}
          {row.dbId && (
            <Button size="sm" variant="ghost" onClick={onDuplicate} className="h-8">
              <Files className="h-3.5 w-3.5 mr-1" />
              Duplicate
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={openPreview} className="h-8">
            Preview
          </Button>
          <Button size="sm" variant="ghost" onClick={copyLink} className="h-8">
            <Copy className="h-3.5 w-3.5" />
          </Button>
          {status !== "archived" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSetStatus("archived")}
              className="h-8 ml-auto text-muted-foreground hover:text-destructive"
              title="Archive"
            >
              <Archive className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function Metric({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: boolean;
}) {
  return (
    <div>
      <div
        className={`text-lg font-semibold tabular-nums ${
          accent ? "text-foreground" : "text-muted-foreground/60"
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
